import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Users, Loader2, Plus } from "lucide-react";
import { useTheme } from "../../../contexts/ThemeContext";

const CreateGroupModal = ({
  groupName,
  setGroupName,
  selectedMembers,
  setSelectedMembers,
  groupDetail,
  handleCreateGroup,
  createGroupLoading,
  onClose,
}) => {
  const { theme } = useTheme();

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: "spring", damping: 20, stiffness: 300 }}
          className={`${theme.contentBg} ${theme.contentBorder}  rounded-xl shadow-2xl w-full max-w-md`}
        >
          {/* Header */}
          <div
            className={`${theme.grpBG} ${theme.headerBorder} border-b px-6 py-4 rounded-t-xl`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`${theme.logoBG} p-2 rounded-full`}>
                  <Users className={`w-5 h-5 ${theme.iconColor}`} />
                </div>
                <div>
                  <h3 className={`text-lg font-semibold ${theme.headerText}`}>
                    Create Group
                  </h3>
                  <p className={`text-xs ${theme.headerText} opacity-75`}>
                    Create a new group with selected members
                  </p>
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={onClose}
                className={`${theme.sidebarText} opacity-60 hover:opacity-100 transition-opacity`}
              >
                <X className="w-5 h-5" />
              </motion.button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {/* Group Name Input */}
            <div className="space-y-2">
              <label className={`text-sm font-medium ${theme.sidebarText}`}>
                Group Name
              </label>
              <motion.input
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                type="text"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="Enter group name"
                className={`w-full p-3 border ${theme.sidebarBorder} rounded-lg ${theme.sidebarBg} ${theme.sidebarText} ${theme.inputFocusRing} focus:border-transparent focus:ring-2 transition-all duration-200`}
              />
            </div>

            {/* Select Members */}
            <div className="space-y-2">
              <label className={`text-sm font-medium ${theme.sidebarText}`}>
                Select Members
              </label>
              <motion.select
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                multiple
                value={selectedMembers}
                onChange={(e) =>
                  setSelectedMembers(
                    Array.from(
                      e.target.selectedOptions,
                      (option) => option.value
                    )
                  )
                }
                className={`w-full p-3 border ${theme.sidebarBorder} rounded-lg ${theme.sidebarBg} ${theme.sidebarText} ${theme.inputFocusRing} focus:border-transparent focus:ring-2 transition-all duration-200 min-h-[120px]`}
              >
                {groupDetail.members.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.name || "Unnamed"}
                  </option>
                ))}
              </motion.select>
              <p className="text-xs text-gray-500">
                Hold Ctrl/Cmd to select multiple members
              </p>
            </div>

            {/* Selected Members Count */}
            {selectedMembers.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-green-50 border border-green-200 rounded-lg p-3"
              >
                <div className="flex items-center space-x-2">
                  <Plus className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-800">
                    {selectedMembers.length} member(s) selected
                  </span>
                </div>
              </motion.div>
            )}
          </div>

          {/* Footer */}
          <div
            className={`${theme.sidebarBg} ${theme.sidebarBorder} border-t px-6 py-4 rounded-b-xl`}
          >
            <div className="flex justify-end space-x-3">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onClose}
                className={`px-4 py-2 ${theme.sidebarText} opacity-60 hover:opacity-100 transition-opacity rounded-lg text-sm font-medium`}
              >
                Cancel
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleCreateGroup}
                disabled={
                  createGroupLoading ||
                  !groupName ||
                  selectedMembers.length === 0
                }
                className={`px-4 py-2 ${theme.accentColor} ${theme.buttonHover} text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 transition-all duration-200`}
              >
                {createGroupLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Creating...</span>
                  </>
                ) : (
                  <>
                    <Users className="w-4 h-4" />
                    <span>Create Group</span>
                  </>
                )}
              </motion.button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CreateGroupModal;

// // CreateGroupModal.js
// import React from 'react';

// const CreateGroupModal = ({
//   groupName,
//   setGroupName,
//   selectedMembers,
//   setSelectedMembers,
//   groupDetail,
//   handleCreateGroup,
//   createGroupLoading,
//   onClose
// }) => {
//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
//       <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
//         <h3 className="text-2xl font-semibold text-indigo-600 mb-4 sm:text-xl">Create Group</h3>

//         {/* Group Name Input */}
//         <div>
//           <label className="block text-lg text-gray-700 mb-2 sm:text-base">Group Name</label>
//           <input
//             type="text"
//             value={groupName}
//             onChange={(e) => setGroupName(e.target.value)}
//             className="w-full p-2 border border-gray-300 rounded-lg mb-4 sm:text-sm"
//             placeholder="Enter group name"
//           />
//         </div>

//         {/* Select Members */}
//         <div>
//           <label className="block text-lg text-gray-700 mb-2 sm:text-base">Select Members</label>
//           <select
//             multiple
//             value={selectedMembers}
//             onChange={(e) => setSelectedMembers(Array.from(e.target.selectedOptions, option => option.value))}
//             className="w-full p-2 border border-gray-300 rounded-lg mb-4 sm:text-sm"
//           >
//             {groupDetail.members.map((member) => (
//               <option key={member.id} value={member.id}>
//                 {member.name || 'Unnamed'}
//               </option>
//             ))}
//           </select>
//         </div>

//         {/* Modal Footer */}
//         <div className="flex justify-end space-x-4">
//           <button
//             onClick={onClose}
//             className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
//           >
//             Cancel
//           </button>
//           <button
//             onClick={handleCreateGroup}
//             className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
//             disabled={createGroupLoading || !groupName || selectedMembers.length === 0}
//           >
//             {createGroupLoading ? (
//               <div className="animate-spin border-t-4 border-white rounded-full w-6 h-6 mx-auto"></div>
//             ) : (
//               'Create Group'
//             )}
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default CreateGroupModal;
